import { useState, useEffect } from "react";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import axios from "axios";
import {
  Calculator as CalculatorIcon,
  IndianRupee as RupeeIcon,
  Leaf,
  TrendingUp,
  Sun,
  Zap,
} from "lucide-react";

const normalizeBaseUrl = (url) => (url || "").trim().replace(/\/+$/, "");
const localHostnames = new Set(["localhost", "127.0.0.1"]);
const isLocalRuntime = localHostnames.has(window.location.hostname);
const configuredApiBase = normalizeBaseUrl(process.env.REACT_APP_BACKEND_URL);
const API = isLocalRuntime ? "http://localhost:8000" : configuredApiBase || "/api";

const RUPEE_SYMBOL = "\u20B9";
const CO2_LABEL = "CO\u2082";
const WARNING_SYMBOL = "\u26A0\uFE0F";
const UNITS_PER_KW_PER_DAY = 4;
const PANEL_WATT = 545;
const DAYS_PER_MONTH = 30;
const DAYS_PER_YEAR = 365;

const calculatorTabs = [
  {
    id: "requirement",
    label: "System Requirement",
    title: "Solar Requirement Calculator",
    description:
      "Enter your annual electricity consumption to find out exactly how many solar panels you need.",
  },
  {
    id: "generation",
    label: "Unit Generation",
    title: "Solar Generation Calculator",
    description:
      "Enter your system size in kW to estimate electricity generation per day, month, and year.",
  },
];

const locations = [
  "Nagpur",
  "Amravati",
  "Bhandara",
  "Wardha",
  "Gadchiroli",
  "Chandrapur",
];

const roundTo = (value, digits = 2) => Number(Number(value).toFixed(digits));

const Calculator = () => {
  const [activeTab, setActiveTab] = useState("requirement");
  const [inputs, setInputs] = useState({
    annual_units: 1800,
    location: "Nagpur",
    electricity_rate: 8,
  });
  const [generationInputs, setGenerationInputs] = useState({
    system_size_kw: 3,
  });
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);
  const [errorHint, setErrorHint] = useState(null);

  const activeTabMeta =
    calculatorTabs.find((tab) => tab.id === activeTab) || calculatorTabs[0];

  useEffect(() => {
    if (results) console.log("Results:", results);
  }, [results]);

  const handleInputChange = (field, value) => {
    setInputs({ ...inputs, [field]: value });
  };

  const handleGenerationInputChange = (value) => {
    setGenerationInputs({
      system_size_kw: value === "" ? "" : Number(value),
    });
  };

  const calculateRequirement = async () => {
    console.log("Sending calculator request:", inputs);
    setLoading(true);
    setResults(null);
    setErrorMsg(null);
    setErrorHint(null);

    try {
      const response = await axios.post(
        `${API}/calculator`,
        {
          annual_units: Number(inputs.annual_units),
          location: inputs.location,
          electricity_rate: Number(inputs.electricity_rate),
        },
        {
          headers: { "Content-Type": "application/json" },
          timeout: 60000,
        }
      );
      console.log("Calculator response:", response.data);
      setResults(response.data);
    } catch (error) {
      console.error("Calculator error:", error.response?.data || error.message);
      if (!error.response) {
        setErrorMsg("Unable to reach the calculator service.");
        setErrorHint("Please check that the backend server is running and try again.");
      } else {
        setErrorMsg(
          error.response?.data?.detail ||
            error.message ||
            "Something went wrong. Please try again."
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const generationSystemSize = Number(generationInputs.system_size_kw) || 0;
  const generationPerDay = roundTo(generationSystemSize * UNITS_PER_KW_PER_DAY, 2);
  const generationPerMonth = roundTo(generationPerDay * DAYS_PER_MONTH, 0);
  const generationPerYear = roundTo(generationPerDay * DAYS_PER_YEAR, 0);
  const estimatedPanelCount =
    generationSystemSize > 0 ? Math.ceil((generationSystemSize * 1000) / PANEL_WATT) : 0;
  const generationCo2Reduction = roundTo((generationPerYear * 0.82) / 1000, 1);

  const fmt = (v) => Number(v).toLocaleString("en-IN");
  const fmtRs = (v) => RUPEE_SYMBOL + Number(v).toLocaleString("en-IN");

  return (
    <div className="min-h-screen" data-testid="calculator-page">
      <Navbar />

      <section className="pt-32 pb-12 bg-gradient-to-br from-primary/5 via-transparent to-secondary/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex flex-wrap items-center justify-center gap-3 rounded-full border border-border/60 bg-white/80 p-2 shadow-sm mb-8">
            {calculatorTabs.map((tab) => {
              const isActive = tab.id === activeTab;
              return (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setActiveTab(tab.id)}
                  className={`rounded-full px-5 py-2.5 text-sm font-semibold transition-all ${
                    isActive
                      ? "bg-primary text-white shadow-lg"
                      : "text-primary hover:bg-primary/10"
                  }`}
                >
                  {tab.label}
                </button>
              );
            })}
          </div>

          <h1 className="font-heading font-bold text-5xl md:text-7xl text-primary mb-6 tracking-tight">
            {activeTabMeta.title}
          </h1>
          <p className="text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto leading-relaxed">
            {activeTabMeta.description}
          </p>
        </div>
      </section>

      <div className="max-w-4xl mx-auto w-full px-4 pb-16 space-y-10">
        {activeTab === "requirement" ? (
          <>
            <div className="bg-card border border-border/50 shadow-xl rounded-3xl p-8 md:p-12">
              <h2 className="font-heading font-semibold text-3xl text-primary mb-8">
                Enter Your Details
              </h2>
              <div className="space-y-8">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Total Units / Year (kWh)
                  </label>
                  <input
                    type="number"
                    value={inputs.annual_units === 0 ? "" : inputs.annual_units}
                    onChange={(e) =>
                      handleInputChange(
                        "annual_units",
                        e.target.value === "" ? "" : Number(e.target.value)
                      )
                    }
                    className="w-full border border-border/50 rounded-xl px-4 py-3 text-gray-800 text-lg font-semibold focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition bg-background"
                    min={600}
                    max={100000}
                    placeholder="e.g. 1800"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Monthly units x 12 from your electricity bill
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Location
                  </label>
                  <div className="relative">
                    <select
                      value={inputs.location}
                      onChange={(e) => handleInputChange("location", e.target.value)}
                      className="w-full border border-border/50 rounded-xl px-4 py-3 text-gray-800 bg-background appearance-none focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition"
                    >
                      {locations.map((loc) => (
                        <option key={loc} value={loc}>
                          {loc}
                        </option>
                      ))}
                    </select>
                    <div className="pointer-events-none absolute inset-y-0 right-4 flex items-center">
                      <svg
                        className="w-4 h-4 text-muted-foreground"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 9l-7 7-7-7"
                        />
                      </svg>
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Electricity Rate ({RUPEE_SYMBOL}/kWh)
                  </label>
                  <input
                    type="number"
                    value={inputs.electricity_rate === 0 ? "" : inputs.electricity_rate}
                    onChange={(e) =>
                      handleInputChange(
                        "electricity_rate",
                        e.target.value === "" ? "" : Number(e.target.value)
                      )
                    }
                    className="w-full border border-border/50 rounded-xl px-4 py-3 text-gray-800 text-lg font-semibold focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition bg-background"
                    min={1}
                    max={20}
                    step={0.5}
                    placeholder="e.g. 8"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Check your electricity bill for per-unit rate
                  </p>
                </div>
              </div>

              <button
                onClick={calculateRequirement}
                disabled={loading}
                className="mt-10 w-full bg-gradient-to-r from-primary to-secondary hover:from-primary/90 hover:to-secondary/90 text-white font-bold py-4 rounded-full shadow-xl transition-all hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed text-base flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Calculating...
                  </>
                ) : (
                  <>
                    <CalculatorIcon className="w-5 h-5" />
                    Calculate Solar Requirement
                  </>
                )}
              </button>
            </div>

            {errorMsg && (
              <div className="bg-red-50 border border-red-200 rounded-2xl p-6 text-center">
                <p className="text-red-600 font-semibold text-sm">
                  {WARNING_SYMBOL} {errorMsg}
                </p>
                {errorHint && (
                  <p className="text-red-400 text-xs mt-2">
                    {errorHint}
                  </p>
                )}
              </div>
            )}

            {results && (
              <div className="space-y-6">
                <div className="text-center">
                  <h2 className="font-heading font-semibold text-3xl md:text-5xl text-primary mb-4 tracking-tight">
                    Your Solar Estimate
                  </h2>
                  <p className="text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed">
                    Based on {fmt(inputs.annual_units)} units/year @ {RUPEE_SYMBOL}
                    {inputs.electricity_rate}/kWh in {inputs.location}
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-card border border-border/50 shadow-sm hover:shadow-xl hover:border-primary/20 transition-all duration-300 rounded-3xl p-8 text-center">
                    <div className="inline-flex items-center justify-center w-16 h-16 bg-primary/10 text-primary rounded-2xl mb-4">
                      <Sun className="w-8 h-8" />
                    </div>
                    <p className="text-4xl font-black text-primary mb-1">
                      {results.system_size_kw} kWp
                    </p>
                    <p className="text-sm font-semibold text-muted-foreground">
                      Recommended System Size
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {results.panel_count} solar panels (545W)
                    </p>
                  </div>

                  <div className="bg-card border border-border/50 shadow-sm hover:shadow-xl hover:border-primary/20 transition-all duration-300 rounded-3xl p-8 text-center">
                    <div className="inline-flex items-center justify-center w-16 h-16 bg-emerald-500/10 text-emerald-600 rounded-2xl mb-4">
                      <RupeeIcon className="w-8 h-8" />
                    </div>
                    <p className="text-4xl font-black text-emerald-600 mb-1">
                      {fmtRs(results.annual_savings)}
                    </p>
                    <p className="text-sm font-semibold text-muted-foreground">
                      Annual Savings
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {fmtRs(results.monthly_savings)}/month
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-card border border-border/50 shadow-sm hover:shadow-xl hover:border-primary/20 transition-all duration-300 rounded-3xl p-8 text-center">
                    <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-500/10 text-blue-500 rounded-2xl mb-4">
                      <TrendingUp className="w-8 h-8" />
                    </div>
                    <p className="text-4xl font-black text-blue-500 mb-1">
                      {results.payback_period} yrs
                    </p>
                    <p className="text-sm font-semibold text-muted-foreground">
                      Payback Period
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {fmt(results.roi_percentage)}% ROI over 25 years
                    </p>
                  </div>

                  <div className="bg-card border border-border/50 shadow-sm hover:shadow-xl hover:border-primary/20 transition-all duration-300 rounded-3xl p-8 text-center">
                    <div className="inline-flex items-center justify-center w-16 h-16 bg-emerald-500/10 text-emerald-600 rounded-2xl mb-4">
                      <Leaf className="w-8 h-8" />
                    </div>
                    <p className="text-4xl font-black text-emerald-600 mb-1">
                      {results.co2_reduction} t
                    </p>
                    <p className="text-sm font-semibold text-muted-foreground">
                      {CO2_LABEL} Reduction/Year
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {fmt(results.estimated_annual_production)} kWh produced
                    </p>
                  </div>
                </div>

                <div className="bg-card border border-border/50 shadow-sm hover:shadow-xl hover:border-primary/20 transition-all duration-300 rounded-3xl p-8 text-center">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-primary/10 text-primary rounded-2xl mb-4">
                    <Zap className="w-8 h-8" />
                  </div>
                  <p className="text-4xl font-black text-primary mb-1">
                    {fmtRs(results.subsidy_amount)}
                  </p>
                  <p className="text-sm font-semibold text-muted-foreground">
                    MNRE Government Subsidy
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Applicable under PM Surya Ghar Yojana for residential systems
                  </p>
                </div>

                <div className="bg-primary rounded-3xl p-12 text-center relative overflow-hidden">
                  <div className="absolute top-0 right-0 w-64 h-64 bg-secondary/20 rounded-full blur-3xl" />
                  <div className="absolute bottom-0 left-0 w-64 h-64 bg-accent/20 rounded-full blur-3xl" />
                  <div className="relative z-10">
                    <p className="font-heading font-bold text-2xl text-primary-foreground mb-2">
                      Units Covered / Year:{" "}
                      <span className="text-secondary">
                        {fmt(results.annual_units_covered)} kWh
                      </span>
                    </p>
                    <p className="text-primary-foreground/80 text-sm mb-8">
                      Each panel generates minimum ~795 units/year based on about 4
                      units/kW/day in Maharashtra conditions
                    </p>
                    <a
                      href="/contact"
                      className="inline-block bg-secondary hover:bg-secondary/90 text-white font-bold px-10 py-4 rounded-full shadow-xl transition-all hover:scale-[1.02]"
                    >
                      Get Free Quote
                    </a>
                  </div>
                </div>
              </div>
            )}
          </>
        ) : (
          <>
            <div className="bg-card border border-border/50 shadow-xl rounded-3xl p-8 md:p-12">
              <h2 className="font-heading font-semibold text-3xl text-primary mb-8">
                Enter System Size
              </h2>

              <div className="space-y-8">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    System Size (kW)
                  </label>
                  <input
                    type="number"
                    value={
                      generationInputs.system_size_kw === 0
                        ? ""
                        : generationInputs.system_size_kw
                    }
                    onChange={(e) => handleGenerationInputChange(e.target.value)}
                    className="w-full border border-border/50 rounded-xl px-4 py-3 text-gray-800 text-lg font-semibold focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition bg-background"
                    min={0.5}
                    max={1000}
                    step={0.1}
                    placeholder="e.g. 3"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Estimates use 4 units/day for every 1 kW installed
                  </p>
                </div>
              </div>
            </div>

            <div className="space-y-6">
              <div className="text-center">
                <h2 className="font-heading font-semibold text-3xl md:text-5xl text-primary mb-4 tracking-tight">
                  Estimated Unit Generation
                </h2>
                <p className="text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed">
                  Approximate generation for a {generationSystemSize || 0} kW solar
                  system
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-card border border-border/50 shadow-sm hover:shadow-xl hover:border-primary/20 transition-all duration-300 rounded-3xl p-8 text-center">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-primary/10 text-primary rounded-2xl mb-4">
                    <Sun className="w-8 h-8" />
                  </div>
                  <p className="text-4xl font-black text-primary mb-1">
                    {fmt(generationPerDay)}
                  </p>
                  <p className="text-sm font-semibold text-muted-foreground">
                    Units Per Day
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    kWh/day estimate
                  </p>
                </div>

                <div className="bg-card border border-border/50 shadow-sm hover:shadow-xl hover:border-primary/20 transition-all duration-300 rounded-3xl p-8 text-center">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-secondary/15 text-secondary rounded-2xl mb-4">
                    <CalculatorIcon className="w-8 h-8" />
                  </div>
                  <p className="text-4xl font-black text-secondary mb-1">
                    {fmt(generationPerMonth)}
                  </p>
                  <p className="text-sm font-semibold text-muted-foreground">
                    Units Per Month
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Based on {DAYS_PER_MONTH} days/month
                  </p>
                </div>

                <div className="bg-card border border-border/50 shadow-sm hover:shadow-xl hover:border-primary/20 transition-all duration-300 rounded-3xl p-8 text-center">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-emerald-500/10 text-emerald-600 rounded-2xl mb-4">
                    <Zap className="w-8 h-8" />
                  </div>
                  <p className="text-4xl font-black text-emerald-600 mb-1">
                    {fmt(generationPerYear)}
                  </p>
                  <p className="text-sm font-semibold text-muted-foreground">
                    Units Per Year
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Based on {DAYS_PER_YEAR} days/year
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-card border border-border/50 shadow-sm hover:shadow-xl hover:border-primary/20 transition-all duration-300 rounded-3xl p-8 text-center">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-500/10 text-blue-500 rounded-2xl mb-4">
                    <TrendingUp className="w-8 h-8" />
                  </div>
                  <p className="text-4xl font-black text-blue-500 mb-1">
                    {fmt(estimatedPanelCount)}
                  </p>
                  <p className="text-sm font-semibold text-muted-foreground">
                    Estimated Panels
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Assuming {PANEL_WATT}W modules
                  </p>
                </div>

                <div className="bg-card border border-border/50 shadow-sm hover:shadow-xl hover:border-primary/20 transition-all duration-300 rounded-3xl p-8 text-center">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-emerald-500/10 text-emerald-600 rounded-2xl mb-4">
                    <Leaf className="w-8 h-8" />
                  </div>
                  <p className="text-4xl font-black text-emerald-600 mb-1">
                    {fmt(generationCo2Reduction)} t
                  </p>
                  <p className="text-sm font-semibold text-muted-foreground">
                    {CO2_LABEL} Reduction/Year
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Approximate annual environmental benefit
                  </p>
                </div>
              </div>

              <div className="bg-primary rounded-3xl p-12 text-center relative overflow-hidden">
                <div className="absolute top-0 right-0 w-64 h-64 bg-secondary/20 rounded-full blur-3xl" />
                <div className="absolute bottom-0 left-0 w-64 h-64 bg-accent/20 rounded-full blur-3xl" />
                <div className="relative z-10">
                  <p className="font-heading font-bold text-2xl text-primary-foreground mb-2">
                    Formula Used:{" "}
                    <span className="text-secondary">
                      kW x {UNITS_PER_KW_PER_DAY} units/kW/day
                    </span>
                  </p>
                  <p className="text-primary-foreground/80 text-sm mb-8">
                    These are planning estimates. Actual generation varies with season,
                    shadow, roof tilt, module quality, and inverter performance.
                  </p>
                  <a
                    href="/contact"
                    className="inline-block bg-secondary hover:bg-secondary/90 text-white font-bold px-10 py-4 rounded-full shadow-xl transition-all hover:scale-[1.02]"
                  >
                    Ask For Site Assessment
                  </a>
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default Calculator;
